import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { Image, Upload, message } from 'antd';
import type { UploadProps } from 'antd';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { deleteUploadedImages, uploadImage } from '../api';
import type { AssetType } from '../api';
import { AvatarCropModal } from './AvatarCropModal';
import { ImageCropModal } from './ImageCropModal';
import type { ImageCropConfig } from './ImageCropModal';
import { compressImagePreservingRatio } from './image-compression';
import { fitWithinDimensions } from './image-compression';
import type { ImageCompressionConfig } from './image-compression';

type Props = { value?: string; onChange?: (value: string) => void; label?: string; assetType?: AssetType; square?: boolean; portraitPreview?: boolean; adaptivePreview?: boolean; crop?: ImageCropConfig; compress?: ImageCompressionConfig };

const KB = 1024;
export const imageCropPresets = {
  avatar: { width: 512, height: 512, aspectLabel: '1:1', maxBytes: 256 * KB },
  icon: { width: 96, height: 96, aspectLabel: '1:1', maxBytes: 64 * KB },
  landscape: { width: 640, height: 360, aspectLabel: '16:9', maxBytes: 320 * KB },
  hero: { width: 640, height: 480, aspectLabel: '4:3', maxBytes: 400 * KB },
} satisfies Record<string, ImageCropConfig>;

export const imageCompressionPresets = {
  watermark: { maxBytes: 1536 * KB, maxDimension: 2560, preserveTransparency: true },
} satisfies Record<string, ImageCompressionConfig>;

export type ImageUploadFieldHandle = {
  commit: () => void;
  discard: () => Promise<void>;
};

export const ImageUploadField = forwardRef<ImageUploadFieldHandle, Props>(function ImageUploadField(
  { value, onChange, label = '上传图片', assetType = 'misc', square = false, portraitPreview = false, adaptivePreview = false, crop, compress },
  ref,
) {
  const isAvatar = assetType === 'avatars';
  const isSquare = isAvatar || square;
  const cropConfig = crop;
  const previewWidth = portraitPreview ? 180 : isSquare ? 96 : cropConfig ? 160 : 116;
  const previewHeight = portraitPreview ? 320 : isSquare ? 96 : cropConfig ? Math.round(160 * cropConfig.height / cropConfig.width) : 82;
  const [loading, setLoading] = useState(false);
  const [adaptivePreviewSize, setAdaptivePreviewSize] = useState({ width: 180, height: 180 });
  const [cropRequest, setCropRequest] = useState<{
    file: File;
    onSuccess?: (body: object) => void;
    onError?: (error: Error) => void;
  } | null>(null);
  const pendingUrl = useRef('');

  const discard = async () => {
    const url = pendingUrl.current;
    pendingUrl.current = '';
    if (url) await deleteUploadedImages([url]);
  };

  useImperativeHandle(ref, () => ({
    commit: () => { pendingUrl.current = ''; },
    discard,
  }), []);

  useEffect(() => () => {
    const url = pendingUrl.current;
    pendingUrl.current = '';
    if (url) void deleteUploadedImages([url], true).catch(() => {});
  }, []);

  const uploadPreparedFile = async (file: File, onSuccess?: (body: object) => void, onError?: (error: Error) => void) => {
    try {
      setLoading(true);
      const preparedFile = compress ? await compressImagePreservingRatio(file, compress) : file;
      const url = await uploadImage(preparedFile, assetType);
      const previousPendingUrl = pendingUrl.current;
      pendingUrl.current = url;
      onChange?.(url);
      onSuccess?.({ url });
      if (previousPendingUrl && previousPendingUrl !== url) {
        void deleteUploadedImages([previousPendingUrl]).catch(() => {});
      }
      message.success('图片上传成功');
    } catch (error) {
      onError?.(error as Error);
      message.error((error as Error).message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const customRequest: UploadProps['customRequest'] = ({ file, onSuccess, onError }) => {
    if (isAvatar || cropConfig) {
      setCropRequest({ file: file as File, onSuccess, onError });
      return;
    }
    void uploadPreparedFile(file as File, onSuccess, onError).catch(() => {});
  };

  return (
    <>
    <div className={`image-upload-field ${isSquare ? 'square-upload-field' : ''} ${portraitPreview ? 'portrait-upload-field' : ''} ${adaptivePreview ? 'adaptive-upload-field' : ''}`}>
      {value ? <Image
        src={value}
        width={adaptivePreview ? adaptivePreviewSize.width : previewWidth}
        height={adaptivePreview ? adaptivePreviewSize.height : previewHeight}
        className="image-cover"
        onLoad={event => {
          if (!adaptivePreview) return;
          const next = fitWithinDimensions(event.currentTarget.naturalWidth, event.currentTarget.naturalHeight, 180, 320);
          if (next.width && next.height) setAdaptivePreviewSize(current => current.width === next.width && current.height === next.height ? current : next);
        }}
      /> : null}
      <Upload accept="image/jpeg,image/png,image/webp,image/gif" showUploadList={false} customRequest={customRequest}>
        <button className="upload-tile" type="button" style={{ width: previewWidth, height: previewHeight }}>
          {loading ? <LoadingOutlined /> : <PlusOutlined />}
          <span>{loading ? '上传中' : label}</span>
        </button>
      </Upload>
    </div>
    {isAvatar && !cropConfig ? <AvatarCropModal
      open={Boolean(cropRequest)}
      file={cropRequest?.file || null}
      onCancel={() => {
        cropRequest?.onError?.(new Error('已取消头像裁剪'));
        setCropRequest(null);
      }}
      onConfirm={async file => {
        if (!cropRequest) return;
        await uploadPreparedFile(file, cropRequest.onSuccess, cropRequest.onError);
        setCropRequest(null);
      }}
    /> : <ImageCropModal
      config={cropConfig || imageCropPresets.avatar}
      label={label.replace(/^上传/, '') || '图片'}
      open={Boolean(cropRequest)}
      file={cropRequest?.file || null}
      onCancel={() => {
        cropRequest?.onError?.(new Error('已取消图片裁剪'));
        setCropRequest(null);
      }}
      onConfirm={async file => {
        if (!cropRequest) return;
        await uploadPreparedFile(file, cropRequest.onSuccess, cropRequest.onError);
        setCropRequest(null);
      }}
    />}
    </>
  );
});

export function formatDate(value: unknown): string {
  if (!value) return '—';
  const date = new Date(value as string | number | Date);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('zh-CN', { hour12: false });
}
